import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Videocam as VideoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Event } from '@/types/events';

interface EventCardProps {
  event: Event;
  onEdit?: (event: Event) => void;
  onDelete?: (id: string) => void;
  onView?: (event: Event) => void;
}

export const EventCard: React.FC<EventCardProps> = ({
  event,
  onEdit,
  onDelete,
  onView
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return 'success';
      case 'pending_approval':
      case 'in_progress':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card 
      sx={{ 
        width: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                {event.title}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={event.status.replace('_', ' ')}
                  color={getStatusColor(event.status)}
                  size="small"
                />
                <Chip
                  label={event.eventCategory}
                  variant="outlined"
                  size="small"
                />
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              {onView && (
                <Tooltip title="View details">
                  <IconButton 
                    size="small" 
                    onClick={() => onView(event)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onEdit && (
                <Tooltip title="Edit event">
                  <IconButton 
                    size="small" 
                    onClick={() => onEdit(event)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete event">
                  <IconButton 
                    size="small" 
                    onClick={() => onDelete(event.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {event.description}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {event.locationType === 'virtual' ? (
                <VideoIcon fontSize="small" color="action" />
              ) : (
                <LocationIcon fontSize="small" color="action" />
              )}
              <Typography variant="body2">
                {event.location || 'Virtual Event'}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              {format(new Date(event.startDate), 'MMM d, yyyy h:mm a')}
            </Typography>
          </Stack>

          {event.targetGrades.length > 0 && (
            <Stack direction="row" spacing={1}>
              {event.targetGrades.map((grade) => (
                <Chip
                  key={grade}
                  label={`Grade ${grade}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};